import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    IconButton
} from "@mui/material";
import { useAlert } from "../alerts/AlertContext";
import CloseIcon from "@mui/icons-material/Close";
import AxiosInstance from "../../services/AxiosInstance";
import { ThemeProvider } from "../v2/home/theme/ThemeProvider";
import { ASSET_STATUS_URL } from "../../Constants/Constants";

const DEFAULT_COLOR = "#4CAF50";

const AssetStatusForm = ({
    open,
    onClose,
    editData = null,
    onSaved
}) => {

    const [formData, setFormData] = useState({
        statusCode: "",
        name: "",
        color: DEFAULT_COLOR,
        description: ""
    });
    const showAlert = useAlert();
    const [saving, setSaving] = useState(false);

    useEffect(() => {

        if (editData) {

            setFormData({
                statusCode: editData.statusCode || "",
                name: editData.name || "",
                color: editData.color || DEFAULT_COLOR,
                description: editData.description || ""
            });

        } else {

            setFormData({
                statusCode: "",
                name: "",
                color: DEFAULT_COLOR,
                description: ""
            });
        }

    }, [editData, open]);

    const handleStatusCodeChange = (event) => {

        const { name, value } = event.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleChange = (event) => {

        const { name, value } = event.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleColorChange = (event) => {

        setFormData(prev => ({
            ...prev,
            color: event.target.value.toUpperCase()
        }));
    };

    const validate = () => {

        if (!formData.name.trim()) {
            showAlert("Status name is required", 'error');
            return false;
        }

        if (!/^#[0-9A-Fa-f]{6}$/.test(formData.color)) {
            showAlert("Enter a valid HEX color", 'error');
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {

        if (!validate()) {
            return;
        }

        try {

            setSaving(true);

            const payload = {
                statusCode: formData.statusCode.trim(),
                name: formData.name.trim(),
                color: formData.color.toUpperCase(),
                description: formData.description.trim()
            };

            let response;

            if (editData?.id) {

                response = await AxiosInstance.put(
                    `${ASSET_STATUS_URL}/${editData.id}`,
                    payload
                );

            } else {

                response = await AxiosInstance.post(
                    ASSET_STATUS_URL,
                    payload
                );
            }
            showAlert(editData
                ? "Asset status updated successfully"
                : "Asset status created successfully", 'success');


            onSaved?.(response.data);

            onClose();

        } catch (error) {

            console.error(error);

            showAlert(
                error?.response?.data?.message ||
                "Unable to save asset status", 'error');


        } finally {

            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            fullWidth
            maxWidth="sm"
        >

            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between"
                }}
            >
                {editData
                    ? "Edit Asset Status"
                    : "Add Asset Status"}

                <IconButton
                    onClick={onClose}
                    disabled={saving}
                >
                    <CloseIcon />
                </IconButton>

            </DialogTitle>

            <DialogContent dividers>
                <ThemeProvider>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            pt: 1
                        }}
                    >

                        <TextField
                            fullWidth
                            label="Status Code"
                            name="statusCode"
                            value={formData.statusCode}
                            onChange={handleStatusCodeChange}
                            required
                            autoFocus
                        />

                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            pt: 1
                        }}
                    >

                        <TextField
                            fullWidth
                            label="Status Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            autoFocus
                        />

                        <Box>

                            <Typography
                                variant="body2"
                                sx={{ mb: 1 }}
                            >
                                Status Color
                            </Typography>

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2
                                }}
                            >

                                {/* Color picker */}
                                <Box
                                    sx={{
                                        width: 52,
                                        height: 52,
                                        borderRadius: 1,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        overflow: "hidden"
                                    }}
                                >
                                    <input
                                        type="color"
                                        value={formData.color}
                                        onChange={handleColorChange}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            border: "none",
                                            padding: 0,
                                            cursor: "pointer"
                                        }}
                                    />
                                </Box>

                                {/* HEX value */}
                                <TextField
                                    label="HEX Color"
                                    name="color"
                                    value={formData.color}
                                    onChange={handleChange}
                                    placeholder="#4CAF50"
                                    inputProps={{
                                        maxLength: 7
                                    }}
                                    sx={{ flex: 1 }}
                                />

                                {/* Preview */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 20,
                                            height: 20,
                                            borderRadius: "50%",
                                            backgroundColor:
                                                formData.color
                                        }}
                                    />

                                    <Typography variant="body2">
                                        Preview
                                    </Typography>
                                </Box>

                            </Box>

                        </Box>

                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                        />

                    </Box>
                </ThemeProvider>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>

                <Button
                    onClick={onClose}
                    disabled={saving}
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving}
                >
                    {saving
                        ? "Saving..."
                        : editData
                            ? "Update"
                            : "Save"}
                </Button>

            </DialogActions>

        </Dialog>
    );
};

export default AssetStatusForm;